import { type FunctionComponent, useMemo, useState } from "react";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { notEmpty } from "shared/lib/utils";

import { AppFooter } from "scmp-app/components/app-footer";
import { PlusArchiveHeader } from "scmp-app/components/plus/plus-archive-header";
import { PlusPaywallSuspense } from "scmp-app/components/plus/plus-paywall-suspense";

import { CftContextProvider } from "./context";
import { PlusCftHeader } from "./header";
import { PlusCftIndustryFilterTabs } from "./industry-filter-tabs";
import {
  Container,
  FilterAndWebinarArea,
  FilterTabsContainer,
  StyledLoading,
  StyledPlusCftContentList,
  StyledPlusCftWebinarWidget,
  WidgetContainer,
  Wrapper,
} from "./styles";

type Props = {
  className?: string;
};

export const PlusCft: FunctionComponent<Props> = ({ className }) => {
  const [industryTopics, setIndustryTopics] = useState<Record<string, string[]>>({});
  const [selectedIndustryNames, setSelectedIndustryNames] = useState<string[]>([]);

  const selectedTopicIds = useMemo(
    () => selectedIndustryNames.flatMap(name => industryTopics[name] || []).filter(notEmpty),
    [industryTopics, selectedIndustryNames],
  );

  return (
    <Container className={className}>
      <PlusPaywallSuspense>
        <CftContextProvider>
          <Wrapper>
            <PlusArchiveHeader title="Plus Archive" />
            <PlusCftHeader />
            <FilterAndWebinarArea>
              <FilterTabsContainer>
                <PlusCftIndustryFilterTabs
                  industryTopics={industryTopics}
                  selectedIndustryNames={selectedIndustryNames}
                  setIndustryTopics={setIndustryTopics}
                  setSelectedIndustryNames={setSelectedIndustryNames}
                />
                <ClientSideSuspense fallback={<StyledLoading />}>
                  <StyledPlusCftContentList selectedTopicIds={selectedTopicIds} />
                </ClientSideSuspense>
              </FilterTabsContainer>
              <WidgetContainer>
                <ClientSideSuspense fallback={<></>}>
                  <StyledPlusCftWebinarWidget />
                </ClientSideSuspense>
              </WidgetContainer>
            </FilterAndWebinarArea>
          </Wrapper>
          <AppFooter variant="plus-without-app-link-row" />
        </CftContextProvider>
      </PlusPaywallSuspense>
    </Container>
  );
};

PlusCft.displayName = "PlusCft";
